// Pure geometry and colour maths for the dashboard PDF (no jsPDF calls), so it can be unit tested.

export type Rgb = readonly [number, number, number]

export function hexToRgb(hex: string): Rgb {
  const value = hex.replace('#', '').trim()
  const full = value.length === 3 ? value.split('').map((char) => char + char).join('') : value
  const parsed = Number.parseInt(full, 16)
  if (full.length !== 6 || Number.isNaN(parsed)) return [0, 0, 0]
  return [(parsed >> 16) & 255, (parsed >> 8) & 255, parsed & 255]
}

/** `color` drawn at `alpha` opacity over `background` (PDF has no cheap per-shape opacity, so tints are pre-blended). */
export function blendRgb(color: Rgb, background: Rgb, alpha: number): Rgb {
  const a = Math.max(0, Math.min(1, alpha))
  return [
    Math.round(color[0] * a + background[0] * (1 - a)),
    Math.round(color[1] * a + background[1] * (1 - a)),
    Math.round(color[2] * a + background[2] * (1 - a)),
  ]
}

/** Linear interpolation between two colours, t in [0, 1]. */
export function mixRgb(from: Rgb, to: Rgb, t: number): Rgb {
  return blendRgb(to, from, t)
}

function niceStep(roughStep: number) {
  if (!Number.isFinite(roughStep) || roughStep <= 0) return 1
  const exponent = Math.floor(Math.log10(roughStep))
  const magnitude = 10 ** exponent
  const fraction = roughStep / magnitude
  const nice = fraction <= 1 ? 1 : fraction <= 2 ? 2 : fraction <= 2.5 ? 2.5 : fraction <= 5 ? 5 : 10
  return nice * magnitude
}

/**
 * Round axis ticks covering [min, max], about `count` of them, like a chart library's 'auto' domain.
 * The domain is the first and last tick. Integer steps only when `allowDecimals` is false.
 */
export function niceTicks(min: number, max: number, count = 5, allowDecimals = true): number[] {
  let low = Number.isFinite(min) ? min : 0
  let high = Number.isFinite(max) ? max : 1
  if (high < low) [low, high] = [high, low]
  if (high === low) {
    if (high === 0) high = 1
    else if (high > 0) low = 0
    else high = 0
  }
  let step = niceStep((high - low) / Math.max(1, count - 1))
  if (!allowDecimals) step = Math.max(1, Math.ceil(step))
  const start = Math.floor(low / step) * step
  const end = Math.ceil(high / step) * step
  const ticks: number[] = []
  for (let value = start; value <= end + step / 1e6; value += step) {
    ticks.push(Number(value.toFixed(10)))
  }
  return ticks.length >= 2 ? ticks : [start, start + step]
}

export type Point = { x: number; y: number }
export type BezierSegment = { c1: Point; c2: Point; to: Point }

/**
 * Cubic Bézier segments for a monotone curve through `points` (sorted by x), the same shape as
 * d3's curveMonotoneX that Recharts uses for `type="monotone"`. Returns one segment per gap.
 */
export function monotoneCurveSegments(points: Point[]): BezierSegment[] {
  const n = points.length
  if (n < 2) return []
  const at = (index: number): Point => points[index] ?? { x: 0, y: 0 }

  const dx: number[] = []
  const slopes: number[] = []
  for (let i = 0; i < n - 1; i += 1) {
    const h = at(i + 1).x - at(i).x
    dx.push(h)
    slopes.push(h === 0 ? 0 : (at(i + 1).y - at(i).y) / h)
  }
  const dxAt = (index: number) => dx[index] ?? 0
  const slopeAt = (index: number) => slopes[index] ?? 0

  // Tangents (Fritsch–Carlson), the same rule as d3-shape's monotone curve.
  const tangents: number[] = new Array<number>(n).fill(0)
  for (let i = 1; i < n - 1; i += 1) {
    const s0 = slopeAt(i - 1)
    const s1 = slopeAt(i)
    const h0 = dxAt(i - 1)
    const h1 = dxAt(i)
    if (s0 * s1 > 0) {
      const p = (s0 * h1 + s1 * h0) / (h0 + h1)
      tangents[i] = (Math.sign(s0) + Math.sign(s1)) * Math.min(Math.abs(s0), Math.abs(s1), 0.5 * Math.abs(p))
    }
  }
  const tangentAt = (index: number) => tangents[index] ?? 0
  const endTangent = (h: number, slope: number, inner: number) => (h ? (3 * slope - inner) / 2 : inner)
  tangents[0] = n === 2 ? slopeAt(0) : endTangent(dxAt(0), slopeAt(0), tangentAt(1))
  tangents[n - 1] = n === 2 ? slopeAt(0) : endTangent(dxAt(n - 2), slopeAt(n - 2), tangentAt(n - 2))

  const segments: BezierSegment[] = []
  for (let i = 0; i < n - 1; i += 1) {
    const a = at(i)
    const b = at(i + 1)
    const third = dxAt(i) / 3
    segments.push({
      c1: { x: a.x + third, y: a.y + third * tangentAt(i) },
      c2: { x: b.x - third, y: b.y - third * tangentAt(i + 1) },
      to: { x: b.x, y: b.y },
    })
  }
  return segments
}

/** A point on a dial whose arc runs from its left end (0°) over the top to its right end (180°). */
export function dialPoint(cx: number, cy: number, radius: number, angleDeg: number): Point {
  const angleRad = ((angleDeg - 180) * Math.PI) / 180
  return { x: cx + radius * Math.cos(angleRad), y: cy + radius * Math.sin(angleRad) }
}

/** Points along a dial arc from `startDeg` to `endDeg`, at most `stepDeg` apart. */
export function dialArcPoints(cx: number, cy: number, radius: number, startDeg: number, endDeg: number, stepDeg = 3): Point[] {
  const span = endDeg - startDeg
  const steps = Math.max(1, Math.ceil(Math.abs(span) / stepDeg))
  return Array.from({ length: steps + 1 }, (_unused, index) => dialPoint(cx, cy, radius, startDeg + (span * index) / steps))
}

/**
 * Outline of a donut slice as a polygon. Angles are in degrees, counter-clockwise from 3 o'clock
 * (Recharts' Pie default), with y pointing down as on screen.
 */
export function donutSlicePolygon(cx: number, cy: number, innerRadius: number, outerRadius: number, startDeg: number, endDeg: number, stepDeg = 2): Point[] {
  const span = endDeg - startDeg
  const steps = Math.max(1, Math.ceil(Math.abs(span) / stepDeg))
  const at = (radius: number, deg: number): Point => {
    const rad = (deg * Math.PI) / 180
    return { x: cx + radius * Math.cos(rad), y: cy - radius * Math.sin(rad) }
  }
  const outer = Array.from({ length: steps + 1 }, (_unused, index) => at(outerRadius, startDeg + (span * index) / steps))
  const inner = Array.from({ length: steps + 1 }, (_unused, index) => at(innerRadius, endDeg - (span * index) / steps))
  return [...outer, ...inner]
}

/**
 * Start/end angles of each donut slice. Like Recharts' Pie: slices run from 0° to 360° in order,
 * `paddingDeg` between neighbours (none when there is a single non-zero slice), zero values drawn as nothing.
 */
export function donutSliceAngles(values: number[], paddingDeg = 2): Array<{ start: number; end: number } | null> {
  const positive = values.map((value) => (Number.isFinite(value) && value > 0 ? value : 0))
  const total = positive.reduce((sum, value) => sum + value, 0)
  const nonZero = positive.filter((value) => value > 0).length
  if (total <= 0) return values.map(() => null)
  const padding = nonZero > 1 ? paddingDeg : 0
  const available = 360 - padding * nonZero
  let cursor = 0
  return positive.map((value) => {
    if (value <= 0) return null
    const sweep = (value / total) * available
    const slice = { start: cursor, end: cursor + sweep }
    cursor += sweep + padding
    return slice
  })
}
